import React, { useState } from 'react';
import { 
  Book, ChevronRight, ChevronDown, Search, Home, ShoppingCart, Package, 
  User, CreditCard, Truck, MapPin, Gift, Heart, Star, Clock,
  CheckCircle, AlertTriangle, Info, Lightbulb, ArrowRight,
  Smartphone, QrCode, Bell, Shield, HelpCircle, X, Phone,
  Mail, MessageCircle, Store, Navigation, Camera, Scan, Play, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

// Guide images
const guideImages = {
  shopping_cart: 'https://static.prod-images.emergentagent.com/jobs/89b70b0d-bf7e-43ce-95a0-a8497f0a05ce/images/1a8bdc7092318642e0efdbeb89fa5dc23027198cf98a27b4cb3a6bc482111b79.png',
  order_tracking: 'https://static.prod-images.emergentagent.com/jobs/89b70b0d-bf7e-43ce-95a0-a8497f0a05ce/images/7b4dd01ba8c9d83a6544716412f056f9e7f7fe747403e9682fc4e6c94e05ab61.png',
  payment_checkout: 'https://static.prod-images.emergentagent.com/jobs/89b70b0d-bf7e-43ce-95a0-a8497f0a05ce/images/d48e785f6055b4c8e09c4e04ddcec119e256635a2f1d2c9db59621fa5fad4eda.png',
  instore_scanning: 'https://static.prod-images.emergentagent.com/jobs/89b70b0d-bf7e-43ce-95a0-a8497f0a05ce/images/c7c17d7de34ec987f59501c4114019e1ffcbe77d11fb11f5503a2a23a1b2ed81.png',
  account_settings: 'https://static.prod-images.emergentagent.com/jobs/89b70b0d-bf7e-43ce-95a0-a8497f0a05ce/images/4fa9f3bad066409221c417dcd1d59949ca114d3ccee35384322e81f4ef0a06ba.png',
};

// Video placeholder component
const VideoPlaceholder = ({ title }) => (
  <div className="bg-gray-900 rounded-lg p-4 mb-4 flex flex-col items-center justify-center min-h-[120px]">
    <Play className="w-10 h-10 text-gray-500 mb-2" />
    <p className="text-gray-400 text-sm font-medium">{title}</p>
    <p className="text-gray-500 text-xs">Video tutorial coming soon</p>
  </div>
);

const CustomerGuide = ({ isOpen, onClose, isModal = true }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(['welcome']);
  const [activeSection, setActiveSection] = useState('welcome');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    setActiveSection(sectionId);
  };

  const guideSections = [
    {
      id: 'welcome',
      title: 'Welcome',
      icon: Home,
      color: 'bg-blue-500',
      content: [
        {
          title: 'Welcome to UPLANDS FOOD MARKET!',
          type: 'intro',
          text: `Thank you for choosing UPLANDS FOOD MARKET - Your Neighborhood African & Caribbean Grocery Store! We offer a wide selection of authentic African and Caribbean groceries, fresh produce, meats, seafood, spices, and more. This guide will help you make the most of your shopping experience.`
        },
        {
          title: 'Why Shop With Us?',
          type: 'list',
          items: [
            { name: 'Authentic Products', desc: 'Genuine African & Caribbean groceries', color: 'green' },
            { name: 'Fresh Quality', desc: 'Fresh produce, meats, and seafood daily', color: 'blue' },
            { name: 'Convenient Delivery', desc: 'Fast delivery to your doorstep', color: 'purple' },
            { name: 'In-Store Pickup', desc: 'Order online, pick up in store', color: 'orange' },
            { name: 'Great Prices', desc: 'Competitive prices on all items', color: 'emerald' }
          ]
        },
        {
          title: 'Getting Started',
          type: 'steps',
          steps: [
            'Create an account or sign in to start shopping',
            'Browse our categories or search for specific items',
            'Add items to your cart',
            'Choose delivery or pickup at checkout',
            'Track your order in real-time'
          ]
        },
        {
          title: 'Quick Start Video',
          type: 'video'
        }
      ]
    },
    {
      id: 'account',
      title: 'Your Account',
      icon: User,
      color: 'bg-indigo-500',
      content: [
        {
          title: 'Creating Your Account',
          type: 'intro',
          text: `Creating an account lets you save your addresses, track orders, earn rewards, and enjoy a personalized shopping experience.`
        },
        {
          title: 'How to Sign Up',
          type: 'steps',
          steps: [
            'Click the "Sign In" button at the top of the page',
            'Select "Create Account" tab',
            'Enter your name, email, and phone number',
            'Create a secure password',
            'Click "Create Account" to finish'
          ]
        },
        {
          title: 'Sign In Options',
          type: 'list',
          items: [
            { name: 'Email & Password', desc: 'Sign in with your registered email', color: 'blue' },
            { name: 'Google Account', desc: 'Quick sign in with Google', color: 'red' },
            { name: 'Guest Checkout', desc: 'Shop without creating an account', color: 'gray' }
          ]
        },
        {
          title: 'Managing Your Profile',
          type: 'info',
          text: `Click on "My Profile" to update your personal information, manage saved addresses, view your order history, and update your preferences.`
        },
        {
          title: 'Account Settings Screenshot',
          type: 'image',
          src: guideImages.account_settings,
          caption: 'Your profile and account settings interface'
        },
        {
          title: 'Forgot Password?',
          type: 'tip',
          text: `No worries! Click "Forgot Password" on the sign-in page, enter your email, and we'll send you a reset code. Enter the code and create a new password.`
        }
      ]
    },
    {
      id: 'shopping',
      title: 'How to Shop',
      icon: ShoppingCart,
      color: 'bg-emerald-500',
      content: [
        {
          title: 'Browsing Products',
          type: 'intro',
          text: `Our store is organized into easy-to-navigate categories. You can browse by category, search for specific items, or use our AI assistant for personalized recommendations.`
        },
        {
          title: 'Finding Products',
          type: 'steps',
          steps: [
            'Use the search bar at the top to find specific items',
            'Browse categories using the filter buttons',
            'Click on any product to see details, nutrition info, and reviews',
            'Use the heart icon to save items to your favorites',
            'Check the "In Stock" badge to see availability'
          ]
        },
        {
          title: 'Product Categories',
          type: 'list',
          items: [
            { name: 'Produce', desc: 'Fresh fruits, vegetables, and tropical items', color: 'green' },
            { name: 'Meats', desc: 'Fresh and frozen meats, poultry', color: 'red' },
            { name: 'Seafood', desc: 'Fresh fish, dried fish, and seafood', color: 'blue' },
            { name: 'Spices', desc: 'Authentic African & Caribbean seasonings', color: 'orange' },
            { name: 'Grains & Flour', desc: 'Rice, cassava flour, and more', color: 'yellow' },
            { name: 'Beverages', desc: 'Drinks, juices, and sodas', color: 'purple' },
            { name: 'Snacks', desc: 'Chips, cookies, and treats', color: 'pink' }
          ]
        },
        {
          title: 'Using the AI Shopping Assistant',
          type: 'tip',
          text: `Click the "AI Help" button at the bottom right of the screen to chat with our AI assistant. Ask for recipe suggestions, product recommendations, or help finding items!`
        },
        {
          title: 'Shopping Experience',
          type: 'image',
          src: guideImages.shopping_cart,
          caption: 'Browse products and add to your cart easily'
        },
        {
          title: 'How to Browse & Add Items',
          type: 'video'
        }
      ]
    },
    {
      id: 'cart',
      title: 'Your Cart',
      icon: ShoppingCart,
      color: 'bg-orange-500',
      content: [
        {
          title: 'Managing Your Cart',
          type: 'intro',
          text: `Your shopping cart holds all the items you want to purchase. You can easily add, remove, or adjust quantities before checkout.`
        },
        {
          title: 'Adding Items to Cart',
          type: 'steps',
          steps: [
            'Click "Add to Cart" on any product',
            'The cart icon shows your total items',
            'Click the cart icon to view your cart',
            'Use +/- buttons to adjust quantities',
            'Click the trash icon to remove items'
          ]
        },
        {
          title: 'Cart Features',
          type: 'list',
          items: [
            { name: 'Save for Later', desc: 'Move items out of cart to purchase later', color: 'blue' },
            { name: 'Quantity Limits', desc: 'Some items may have purchase limits', color: 'orange' },
            { name: 'Price Updates', desc: 'Prices update automatically if they change', color: 'green' },
            { name: 'Stock Alerts', desc: 'We\'ll notify you if an item goes out of stock', color: 'red' }
          ]
        },
        {
          title: 'Cart Persistence',
          type: 'info',
          text: `Your cart is automatically saved! If you leave the site and come back later, your items will still be there waiting for you (when signed in).`
        }
      ]
    },
    {
      id: 'checkout',
      title: 'Checkout',
      icon: CreditCard,
      color: 'bg-green-500',
      content: [
        {
          title: 'Completing Your Purchase',
          type: 'intro',
          text: `Our checkout process is quick and secure. Choose your delivery method, enter your details, and pay safely with multiple payment options.`
        },
        {
          title: 'Checkout Steps',
          type: 'steps',
          steps: [
            'Review your cart and click "Checkout"',
            'Choose delivery type: Delivery or Store Pickup',
            'Enter or select your delivery address',
            'Choose a delivery time slot (for delivery orders)',
            'Select your payment method',
            'Review your order and apply any promo codes',
            'Click "Place Order" to complete'
          ]
        },
        {
          title: 'Payment Methods',
          type: 'list',
          items: [
            { name: 'Credit/Debit Card', desc: 'Visa, Mastercard, American Express', color: 'blue' },
            { name: 'Cash on Delivery', desc: 'Pay when your order arrives', color: 'green' },
            { name: 'Gift Card', desc: 'Use your UPLANDS gift card balance', color: 'purple' },
            { name: 'E-Transfer', desc: 'Interac e-Transfer for delivery orders', color: 'orange' }
          ]
        },
        {
          title: 'Secure Payments',
          type: 'info',
          text: `All card payments are processed securely through Stripe. We never store your full card details. Look for the lock icon in your browser to confirm secure connection.`
        },
        {
          title: 'Promo Codes',
          type: 'tip',
          text: `Have a promo code? Enter it in the "Promo Code" field at checkout to get your discount. Codes are case-sensitive and may have expiration dates.`
        },
        {
          title: 'Checkout Process',
          type: 'image',
          src: guideImages.payment_checkout,
          caption: 'Secure and easy checkout with multiple payment options'
        },
        {
          title: 'Checkout Tutorial',
          type: 'video'
        }
      ]
    },
    {
      id: 'delivery',
      title: 'Delivery & Pickup',
      icon: Truck,
      color: 'bg-cyan-500',
      content: [
        {
          title: 'Getting Your Order',
          type: 'intro',
          text: `We offer flexible options to get your groceries - fast delivery to your door or convenient in-store pickup.`
        },
        {
          title: 'Delivery Options',
          type: 'list',
          items: [
            { name: 'Standard Delivery', desc: 'Delivered within your selected time slot', color: 'blue' },
            { name: 'Express Delivery', desc: 'Faster delivery for urgent orders', color: 'purple' },
            { name: 'Scheduled Delivery', desc: 'Choose a specific date and time', color: 'green' }
          ]
        },
        {
          title: 'Delivery Zones',
          type: 'info',
          text: `We deliver to multiple zones in the Ottawa area. Enter your address at checkout to see available delivery times and fees for your location.`
        },
        {
          title: 'Store Pickup',
          type: 'steps',
          steps: [
            'Select "Store Pickup" at checkout',
            'Choose your preferred pickup time',
            'Complete your order and receive confirmation',
            'Come to our store at the scheduled time',
            'Show your order QR code or order number',
            'Collect your groceries!'
          ]
        },
        {
          title: 'Delivery Tips',
          type: 'tip',
          text: `Add delivery instructions (buzz code, "leave at door", etc.) in your address to help our drivers deliver smoothly. You can also track your driver in real-time!`
        }
      ]
    },
    {
      id: 'tracking',
      title: 'Order Tracking',
      icon: MapPin,
      color: 'bg-purple-500',
      content: [
        {
          title: 'Track Your Order',
          type: 'intro',
          text: `Stay updated on your order status from the moment you place it until it arrives at your door. We provide real-time tracking and notifications.`
        },
        {
          title: 'Order Statuses',
          type: 'list',
          items: [
            { name: 'Order Placed', desc: 'We received your order', color: 'gray' },
            { name: 'Confirmed', desc: 'Payment verified, order confirmed', color: 'blue' },
            { name: 'Being Prepared', desc: 'Our team is picking your items', color: 'orange' },
            { name: 'Ready', desc: 'Order is packed and ready', color: 'indigo' },
            { name: 'Out for Delivery', desc: 'Driver is on the way', color: 'purple' },
            { name: 'Delivered', desc: 'Order completed!', color: 'green' }
          ]
        },
        {
          title: 'How to Track',
          type: 'steps',
          steps: [
            'Go to "My Orders" from the menu',
            'Find your order in the list',
            'Click "Track Order" to see live status',
            'View driver location on the map (for deliveries)',
            'Receive notifications at each step'
          ]
        },
        {
          title: 'Email & SMS Updates',
          type: 'info',
          text: `We'll send you email updates when your order status changes. Make sure your email is correct in your profile to receive notifications.`
        },
        {
          title: 'Order Tracking Interface',
          type: 'image',
          src: guideImages.order_tracking,
          caption: 'Track your order status in real-time'
        }
      ]
    },
    {
      id: 'instore',
      title: 'In-Store Shopping',
      icon: Store,
      color: 'bg-amber-500',
      content: [
        {
          title: 'Shop In Our Store',
          type: 'intro',
          text: `Visit us in person! Use our In-Store Shopping feature to scan products as you shop, skip the checkout line, and pay directly from your phone.`
        },
        {
          title: 'How In-Store Shopping Works',
          type: 'steps',
          steps: [
            'Come to our store location',
            'Click "In-Store Shopping" on the website',
            'Use your phone camera to scan product barcodes',
            'Items are automatically added to your cart',
            'When done, choose "Self Checkout" or "Send to Cashier"',
            'Pay on your phone or at the register',
            'Show your receipt and go!'
          ]
        },
        {
          title: 'Checkout Options',
          type: 'list',
          items: [
            { name: 'Self Checkout', desc: 'Pay directly on your phone with card', color: 'green' },
            { name: 'Send to Cashier', desc: 'Get a code and pay at register', color: 'blue' },
            { name: 'Cash Payment', desc: 'Pay cash at the cashier', color: 'orange' }
          ]
        },
        {
          title: 'Benefits',
          type: 'tip',
          text: `In-Store Shopping lets you see prices as you shop, keep a running total, and avoid long checkout lines. It's the fastest way to shop in our store!`
        },
        {
          title: 'In-Store Scanning Interface',
          type: 'image',
          src: guideImages.instore_scanning,
          caption: 'Scan products with your phone camera'
        },
        {
          title: 'In-Store Shopping Guide',
          type: 'video'
        }
      ]
    },
    {
      id: 'giftcards',
      title: 'Gift Cards',
      icon: Gift,
      color: 'bg-pink-500',
      content: [
        {
          title: 'UPLANDS Gift Cards',
          type: 'intro',
          text: `Give the gift of great food! UPLANDS gift cards are perfect for friends and family who love authentic African & Caribbean groceries.`
        },
        {
          title: 'Buying Gift Cards',
          type: 'steps',
          steps: [
            'Click "Gift Cards" in the menu',
            'Choose a gift card amount',
            'Add a personal message (optional)',
            'Enter recipient\'s email',
            'Complete payment',
            'Gift card is sent instantly via email!'
          ]
        },
        {
          title: 'Using Gift Cards',
          type: 'steps',
          steps: [
            'Add items to your cart',
            'Go to checkout',
            'Select "Gift Card" as payment method',
            'Enter your gift card code',
            'Remaining balance is applied to your order'
          ]
        },
        {
          title: 'Gift Card Balance',
          type: 'info',
          text: `Check your gift card balance anytime in the Gift Cards section. If your order total is less than your balance, the remaining amount stays on your card for future purchases.`
        }
      ]
    },
    {
      id: 'favorites',
      title: 'Favorites & Lists',
      icon: Heart,
      color: 'bg-red-500',
      content: [
        {
          title: 'Save Your Favorites',
          type: 'intro',
          text: `Save products you love for easy access later. Create shopping lists for different occasions and quickly reorder your favorite items.`
        },
        {
          title: 'Adding Favorites',
          type: 'steps',
          steps: [
            'Find a product you like',
            'Click the heart icon on the product card',
            'The item is saved to your favorites',
            'Access favorites from "My Profile" menu'
          ]
        },
        {
          title: 'Quick Reorder',
          type: 'info',
          text: `Go to "My Orders" and click "Reorder" on any past order to quickly add all those items back to your cart. Perfect for your regular grocery run!`
        },
        {
          title: 'Shopping Tips',
          type: 'tip',
          text: `Save your frequently purchased items to favorites for faster shopping. You can also see your purchase history to remember what you bought before.`
        }
      ]
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: Bell,
      color: 'bg-violet-500',
      content: [
        {
          title: 'Stay Informed',
          type: 'intro',
          text: `Enable notifications to receive updates about your orders, special promotions, and exclusive deals.`
        },
        {
          title: 'Notification Types',
          type: 'list',
          items: [
            { name: 'Order Updates', desc: 'Status changes for your orders', color: 'blue' },
            { name: 'Promotions', desc: 'Special deals and discounts', color: 'green' },
            { name: 'Back in Stock', desc: 'When your favorite items return', color: 'orange' },
            { name: 'Price Drops', desc: 'When items in your favorites go on sale', color: 'purple' }
          ]
        },
        {
          title: 'Enabling Notifications',
          type: 'steps',
          steps: [
            'Click the bell icon in the top menu',
            'Click "Enable Notifications" when prompted',
            'Allow notifications in your browser',
            'You\'ll now receive push notifications!'
          ]
        },
        {
          title: 'Managing Preferences',
          type: 'info',
          text: `You can customize which notifications you receive in your Profile settings. Turn off categories you don't want to hear about.`
        }
      ]
    },
    {
      id: 'help',
      title: 'Help & Support',
      icon: HelpCircle,
      color: 'bg-gray-500',
      content: [
        {
          title: 'We\'re Here to Help',
          type: 'intro',
          text: `Have questions or need assistance? Our support team is ready to help you with any issues or inquiries.`
        },
        {
          title: 'Contact Options',
          type: 'list',
          items: [
            { name: 'AI Assistant', desc: 'Click "AI Help" for instant answers', color: 'blue' },
            { name: 'Email Support', desc: 'support@uplandsfoodmarket.com', color: 'green' },
            { name: 'Phone', desc: 'Call us during business hours', color: 'purple' },
            { name: 'In-Store', desc: 'Visit us for in-person help', color: 'orange' }
          ]
        },
        {
          title: 'Common Questions',
          type: 'list',
          items: [
            { name: 'Where\'s my order?', desc: 'Check "My Orders" for tracking', color: 'blue' },
            { name: 'Can I change my order?', desc: 'Contact us before it\'s prepared', color: 'orange' },
            { name: 'How do I return items?', desc: 'See our return policy below', color: 'red' },
            { name: 'Payment failed?', desc: 'Try another card or contact bank', color: 'yellow' }
          ]
        },
        {
          title: 'Returns & Refunds',
          type: 'warning',
          text: `If you receive damaged or incorrect items, please contact us within 24 hours with photos. We'll arrange a refund or replacement. Fresh items cannot be returned unless damaged.`
        },
        {
          title: 'Store Hours',
          type: 'info',
          text: `Visit us at our Merivale Road location. Check our website or call for current store hours. We're here to serve you!`
        }
      ]
    }
  ];

  const filteredSections = guideSections.filter(section =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.content.some(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.text && item.text.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  );

  const renderContent = (content) => {
    return content.map((item, index) => {
      switch (item.type) {
        case 'intro':
          return (
            <div key={index} className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">{item.title}</h4>
              <p className="text-blue-800 text-sm">{item.text}</p>
            </div>
          );
        case 'steps':
          return (
            <div key={index} className="mb-5">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {item.title}
              </h4>
              <ol className="space-y-2 ml-2">
                {item.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-medium">
                      {i + 1}
                    </span>
                    <span className="text-gray-700 text-sm pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          );
        case 'list':
          return (
            <div key={index} className="mb-5">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">{item.title}</h4>
              <div className="grid gap-1.5">
                {item.items.map((listItem, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <Badge className={`bg-${listItem.color}-100 text-${listItem.color}-700 border-0 text-xs`}>
                      {listItem.name}
                    </Badge>
                    <span className="text-gray-600 text-xs">{listItem.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        case 'info':
          return (
            <div key={index} className="bg-gray-50 border border-gray-200 p-3 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-sm">
                <Info className="w-4 h-4 text-blue-500" />
                {item.title}
              </h4>
              <p className="text-gray-700 text-sm">{item.text}</p>
            </div>
          );
        case 'tip':
          return (
            <div key={index} className="bg-green-50 border border-green-200 p-3 rounded-lg mb-4">
              <h4 className="font-semibold text-green-900 mb-1 flex items-center gap-2 text-sm">
                <Lightbulb className="w-4 h-4 text-green-500" />
                {item.title}
              </h4>
              <p className="text-green-800 text-sm">{item.text}</p>
            </div>
          );
        case 'warning':
          return (
            <div key={index} className="bg-amber-50 border border-amber-200 p-3 rounded-lg mb-4">
              <h4 className="font-semibold text-amber-900 mb-1 flex items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                {item.title}
              </h4>
              <p className="text-amber-800 text-sm">{item.text}</p>
            </div>
          );
        case 'image':
          return (
            <div key={index} className="mb-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                <Image className="w-4 h-4 text-blue-500" />
                {item.title}
              </h4>
              <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                <img src={item.src} alt={item.title} className="w-full h-auto" loading="lazy" />
              </div>
              {item.caption && <p className="text-gray-500 text-xs mt-1 text-center">{item.caption}</p>}
            </div>
          );
        case 'video':
          return (
            <div key={index} className="mb-4">
              <VideoPlaceholder title={item.title} />
            </div>
          );
        default:
          return null;
      }
    });
  };

  if (!isOpen) return null;

  const content = (
    <div className={`${isModal ? 'max-h-[80vh]' : 'min-h-screen'} flex flex-col`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 text-white sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Book className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Customer Guide</h2>
              <p className="text-blue-100 text-xs">Everything you need to know</p>
            </div>
          </div>
          {isModal && (
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
          <input
            type="text"
            placeholder="Search guide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-blue-200 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-4">
          {/* Quick Links */}
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Quick Links</h3>
            <div className="flex flex-wrap gap-2">
              {['shopping', 'checkout', 'tracking', 'help'].map(sectionId => {
                const section = guideSections.find(s => s.id === sectionId);
                if (!section) return null;
                const Icon = section.icon;
                return (
                  <button
                    key={sectionId}
                    onClick={() => toggleSection(sectionId)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      activeSection === sectionId 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {section.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSections.includes(section.id);
              
              return (
                <div key={section.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${section.color}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-semibold text-gray-900 text-sm">{section.title}</h3>
                      <p className="text-xs text-gray-500">{section.content.length} topics</p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                      {renderContent(section.content)}
                    </div>
                  )}
                </div>
              );
            })}

            {filteredSections.length === 0 && (
              <div className="text-center py-8">
                <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No results found</p>
                <p className="text-gray-400 text-xs">Try a different search term</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 p-3 text-center">
        <p className="text-xs text-gray-500">
          Need more help? Contact us at <span className="text-blue-600">support@uplandsfoodmarket.com</span>
        </p>
      </div>
    </div>
  );

  if (!isModal) {
    return content;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
};

export default CustomerGuide;
